import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { nextSlide, setCurrentSlide } from '../store/frontPageSlice';

export const useCarousel = (autoAdvance = true, interval = 6000) => {
  const dispatch = useDispatch();
  
  // Use separate selectors
  const slides = useSelector((state) => state.frontPage.slides);
  const currentSlide = useSelector((state) => state.frontPage.currentSlide);

  // Auto-advance slides
  useEffect(() => {
    if (!autoAdvance || slides.length <= 1) return;

    const timer = setInterval(() => {
      dispatch(nextSlide());
    }, interval);

    return () => clearInterval(timer);
  }, [autoAdvance, interval, slides.length, dispatch]);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      dispatch(setCurrentSlide(index));
    }
  }, [dispatch, slides.length]);

  const next = useCallback(() => {
    dispatch(nextSlide());
  }, [dispatch]);

  const previous = useCallback(() => {
    const newIndex = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    dispatch(setCurrentSlide(newIndex));
  }, [dispatch, currentSlide, slides.length]);

  // Keyboard navigation (optional)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') previous();
      if (e.key === 'ArrowRight') next();
      if (e.key >= '1' && e.key <= '9') {
        const index = parseInt(e.key) - 1;
        if (index < slides.length) goToSlide(index);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, previous, goToSlide, slides.length]);

  return {
    currentSlide,
    currentSlideData: slides[currentSlide] || slides[0],
    goToSlide,
    next,
    previous,
    totalSlides: slides.length,
    canGoNext: slides.length > 1,
    canGoPrevious: slides.length > 1,
  };
};